import classNames from 'clsx';
import React, { memo, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { CONTENT_LOAD_TIMEOUT } from 'reactApp/ui/ReactViewer/ReactViewer.constants';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';

import styles from './ViewerPdf.css';

interface Props {
    url: string;
    renderError?: Function;
    fillParent?: boolean;
}

export const ViewerPdf = memo(({ url, renderError = () => null, fillParent = false }: Props): ReactElement | null => {
    const [isError, setError] = useState(false);
    const [isLoaded, setLoaded] = useState(false);
    const ref = useRef<number | undefined>();

    const handleError = useCallback(() => {
        setError(true);
        setLoaded(true);
    }, [setError, setLoaded]);

    const handleLoad = useCallback(() => {
        if (ref.current) {
            clearTimeout(ref.current);
        }
        setLoaded(true);
    }, [ref.current, setLoaded]);

    useEffect(() => {
        if (ref.current) {
            clearTimeout(ref.current);
        }
        ref.current = window.setTimeout(handleError, CONTENT_LOAD_TIMEOUT);

        return () => {
            if (ref.current) {
                clearTimeout(ref.current);
            }
        };
    }, [url, handleError]);

    if (isError) {
        return renderError();
    }

    return (
        <div
            className={classNames({
                [styles.root]: true,
                [styles.root_fillParent]: fillParent,
            })}
        >
            <iframe
                src={url}
                className={styles.pdf}
                allowFullScreen
                allow="autoplay; fullscreen"
                frameBorder={0}
                onError={handleError}
                onLoad={handleLoad}
            />
            {!isLoaded && <Spinner />}
        </div>
    );
});

ViewerPdf.displayName = 'ViewerPdf';

ViewerPdf.whyDidYouRender = true;
